/**
 * Utilities for parsing and validating suggestion payloads.
 */

import type {
  SuggestionPayload,
  SkillRatingSuggestionPayload,
  SkillPackageSuggestionPayload,
} from '../types/suggestions';

/**
 * Parse a raw JSON string into a SuggestionPayload.
 * Throws if the string is not valid JSON.
 */
export function parsePayload(raw: string): SuggestionPayload {
  return JSON.parse(raw) as SuggestionPayload;
}

/**
 * Validate a skill rating payload.
 * Returns true if the payload has all required fields with valid values.
 */
export function validateSkillRatingPayload(p: unknown): p is SkillRatingSuggestionPayload {
  if (!p || typeof p !== 'object') return false;
  const obj = p as Record<string, unknown>;
  if (typeof obj.skillName !== 'string' || obj.skillName.trim() === '') return false;
  if (typeof obj.skillKey !== 'string' || obj.skillKey.trim() === '') return false;
  if (typeof obj.rating !== 'number') return false;
  if (!Number.isInteger(obj.rating) || obj.rating < 1 || obj.rating > 5) return false;
  return true;
}

/**
 * Validate a skill package payload.
 * Returns true if the payload has a non-empty name and at least one category with at least one skill.
 */
export function validateSkillPackagePayload(p: unknown): p is SkillPackageSuggestionPayload {
  if (!p || typeof p !== 'object') return false;
  const obj = p as Record<string, unknown>;
  if (typeof obj.name !== 'string' || obj.name.trim() === '') return false;
  if (!Array.isArray(obj.categories) || obj.categories.length === 0) return false;
  for (const cat of obj.categories) {
    if (!cat || typeof cat !== 'object') return false;
    const c = cat as Record<string, unknown>;
    if (typeof c.name !== 'string' || c.name.trim() === '') return false;
    if (!Array.isArray(c.skills) || c.skills.length === 0) return false;
  }
  return true;
}
